import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, setCurrentUser } from '../../ducks/user.duck';
import omit from 'lodash/omit';
import axios from 'axios';
import { getUploadURL } from '../../util/api';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = actionPayload => dispatch => {
  return dispatch(uploadImageThunk(actionPayload));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// ================ S3 File Upload Thunk ================ //

export function requestFileUpload(actionPayload) {
  const s3BucketUrl = `https://${process.env.REACT_APP_AWS_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com`;
  return async (dispatch, getState, sdk) => {
    dispatch(uploadFileRequest(actionPayload));

    try {
      // still get signed PUT URL from backend to upload directly to S3
      const response = await getUploadURL({
        key: `profile/files/${actionPayload.id}`,
        type: actionPayload.type,
      });

      const { url } = response;

      if (!url) {
        return dispatch(uploadFileError('Failed to get upload URL'));
      }

      // upload the file
      await axios.put(url, actionPayload.file, {
        headers: {
          'Content-Disposition': 'inline',
          'Content-Type': actionPayload.type,
        },
      });

      const publicUrl = `${s3BucketUrl}/profile/files/${actionPayload.id}`;

      return dispatch(
        uploadFileSuccess({
          data: { id: actionPayload.id, url: publicUrl, file: actionPayload.file },
        })
      );
    } catch (error) {
      console.error(error);
      return dispatch(uploadFileError(error));
    }
  };
}

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
    uploadedFiles: {},
    uploadedFilesOrder: [],
    uploadFileError: null,
    removedFilesIds: [],
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
    },
    uploadFileRequest: (state, action) => {
      // action.payload: { id: 'tempId', file, ... }
      const params = action.payload;
      state.uploadedFiles = {
        ...state.uploadedFiles,
        [params.id]: { ...params },
      };
      state.uploadedFilesOrder = state.uploadedFilesOrder.concat([params.id]);
      state.uploadFileError = null;
    },
    uploadFileSuccess: (state, action) => {
      // action.payload: { data: { id, url, ...rest } }
      const { id, url, ...rest } = action.payload.data || action.payload;
      state.uploadedFiles = { ...state.uploadedFiles, [id]: { id, url, ...rest } };
    },
    uploadFileError: (state, action) => {
      const payload = action.payload;
      if (payload && payload.id) {
        const { id, error } = payload;
        state.uploadedFilesOrder = state.uploadedFilesOrder.filter(i => i !== id);
        state.uploadedFiles = omit(state.uploadedFiles, id);
        state.uploadFileError = error;
      } else {
        state.uploadFileError = payload;
      }
    },
    removeFile: (state, action) => {
      const id = action.payload.id || action.payload;
      const removedFilesIds = state.uploadedFiles[id]
        ? state.removedFilesIds
        : state.removedFilesIds.concat(id);
      state.uploadedFiles = omit(state.uploadedFiles, id);
      state.uploadedFilesOrder = state.uploadedFilesOrder.filter(i => i !== id);
      state.removedFilesIds = removedFilesIds;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      });
  },
});

export const {
  clearUpdatedForm,
  uploadFileRequest,
  uploadFileSuccess,
  uploadFileError,
  removeFile,
} = profileSettingsPageSlice.actions;

export default profileSettingsPageSlice.reducer;
